import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Minus,
  Move,
  MousePointer,
  Link,
  Trash2,
  Copy,
  Edit,
  Play,
  Pause,
  Settings,
  AlertCircle,
  CheckCircle,
  Clock,
  Mail,
  MessageSquare,
  Phone,
  Webhook,
  Database,
  Filter,
  GitBranch,
  Timer,
  Users,
  Zap,
  Bell,
  Repeat
} from 'lucide-react';
import { Workflow, WorkflowStep, WorkflowConnection, BuilderMode } from '../types/workflow';
import { ConfirmationDialog } from './ConfirmationDialog';

interface WorkflowCanvasProps {
  workflow: Workflow;
  selectedStep?: WorkflowStep;
  selectedConnection?: WorkflowConnection;
  mode: BuilderMode;
  zoom: number;
  pan: { x: number; y: number };
  onStepSelect: (step: WorkflowStep | undefined) => void;
  onStepAdd: (step: WorkflowStep) => void;
  onStepUpdate: (stepId: string, updates: Partial<WorkflowStep>) => void;
  onStepDelete: (stepId: string) => void;
  onConnectionSelect: (connection: WorkflowConnection | undefined) => void;
  onConnectionAdd: (connection: WorkflowConnection) => void;
  onConnectionDelete: (connectionId: string) => void;
  onZoomChange: (zoom: number) => void;
  onPanChange: (pan: { x: number; y: number }) => void;
}

interface DragState {
  isDragging: boolean;
  draggedStep?: WorkflowStep;
  dragOffset: { x: number; y: number };
  startPosition: { x: number; y: number };
  currentPosition?: { x: number; y: number }; // Local position for smooth dragging
}

interface ConnectionDragState {
  isConnecting: boolean;
  sourceStep?: WorkflowStep;
  targetStep?: WorkflowStep;
  mousePosition: { x: number; y: number };
}

export const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({
  workflow,
  selectedStep,
  selectedConnection,
  mode,
  zoom,
  pan,
  onStepSelect,
  onStepAdd,
  onStepUpdate,
  onStepDelete,
  onConnectionSelect,
  onConnectionAdd,
  onConnectionDelete,
  onZoomChange,
  onPanChange
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragOffset: { x: 0, y: 0 },
    startPosition: { x: 0, y: 0 }
  });
  const [connectionDragState, setConnectionDragState] = useState<ConnectionDragState>({
    isConnecting: false,
    mousePosition: { x: 0, y: 0 }
  });
  const [hoveredStep, setHoveredStep] = useState<string | undefined>();
  const [isPanning, setIsPanning] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Get step icon based on type
  const getStepIcon = (type: string) => {
    switch (type) {
      case 'email':
        return Mail;
      case 'sms':
        return MessageSquare;
      case 'call':
        return Phone;
      case 'webhook':
        return Webhook;
      case 'delay':
        return Timer;
      case 'condition':
        return GitBranch;
      case 'filter':
        return Filter;
      case 'database':
        return Database;
      case 'trigger':
        return Zap;
      default:
        return Settings;
    }
  };

  // Get step color based on type and status
  const getStepColor = (step: WorkflowStep) => {
    const baseColors = {
      email: 'from-blue-500 to-blue-600',
      sms: 'from-green-500 to-green-600',
      call: 'from-purple-500 to-purple-600',
      webhook: 'from-orange-500 to-orange-600',
      delay: 'from-yellow-500 to-yellow-600',
      condition: 'from-pink-500 to-pink-600',
      filter: 'from-indigo-500 to-indigo-600',
      database: 'from-gray-500 to-gray-600',
      trigger: 'from-red-500 to-red-600'
    };

    return baseColors[step.type as keyof typeof baseColors] || 'from-neutral-500 to-neutral-600';
  };

  // Get current position of a step (considering if it's being dragged)
  const getCurrentStepPosition = useCallback((step: WorkflowStep) => {
    const isDragging = dragState.draggedStep?.id === step.id;
    if (isDragging && dragState.currentPosition) {
      return dragState.currentPosition;
    }
    return step.position || { x: 0, y: 0 };
  }, [dragState]);

  // Handle connection start
  const handleConnectionStart = useCallback((step: WorkflowStep) => {
    if (mode !== 'connect') return;

    console.log('Starting connection from step:', step.name, step.id);
    setConnectionDragState({
      isConnecting: true,
      sourceStep: step,
      mousePosition: { x: step.position?.x || 0, y: step.position?.y || 0 }
    });
  }, [mode]);

  // Handle connection end
  const handleConnectionEnd = useCallback((step: WorkflowStep) => {
    console.log('Attempting to end connection at step:', step.name, step.id);
    console.log('Current connection state:', connectionDragState);
    
    if (connectionDragState.isConnecting && connectionDragState.sourceStep && connectionDragState.sourceStep.id !== step.id) {
      console.log('Creating connection from', connectionDragState.sourceStep.name, 'to', step.name);
      
      // Create the connection immediately
      const connection: WorkflowConnection = {
        id: `conn_${Date.now()}`,
        sourceStepId: connectionDragState.sourceStep.id,
        targetStepId: step.id,
        condition: undefined,
        label: ''
      };
      
      console.log('Creating connection:', connection);
      onConnectionAdd(connection);
      
      // Reset connection state
      setConnectionDragState({
        isConnecting: false,
        mousePosition: { x: 0, y: 0 }
      });
    } else {
      console.log('Connection conditions not met:', {
        isConnecting: connectionDragState.isConnecting,
        hasSourceStep: !!connectionDragState.sourceStep,
        sameStep: connectionDragState.sourceStep?.id === step.id
      });
    }
  }, [connectionDragState, onConnectionAdd]);

  // Handle step mouse down (for both selection and dragging)
  const handleStepMouseDown = useCallback((e: React.MouseEvent, step: WorkflowStep) => {
    e.preventDefault();
    e.stopPropagation();

    if (mode === 'select') {
      // Always select the step first
      onStepSelect(step);

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Convert mouse position to transformed coordinate space
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      // Convert to step coordinate space (accounting for transform)
      const stepX = (mouseX - pan.x) / zoom;
      const stepY = (mouseY - pan.y) / zoom;

      setDragState({
        isDragging: true,
        draggedStep: step,
        dragOffset: {
          x: stepX - (step.position?.x || 0),
          y: stepY - (step.position?.y || 0)
        },
        startPosition: { x: step.position?.x || 0, y: step.position?.y || 0 }
      });
    } else if (mode === 'connect') {
      // Handle connection mode
      if (!connectionDragState.isConnecting) {
        // Start a new connection from this step
        handleConnectionStart(step);
      } else {
        // Complete the connection to this step
        handleConnectionEnd(step);
      }
    }
  }, [mode, pan, zoom, onStepSelect, connectionDragState, handleConnectionStart, handleConnectionEnd]);

  // Handle mouse move for dragging
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Convert mouse position to canvas coordinate space
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Convert to step coordinate space (accounting for transform)
    const stepX = (mouseX - pan.x) / zoom;
    const stepY = (mouseY - pan.y) / zoom;

    // Handle step dragging
    if (dragState.isDragging && dragState.draggedStep) {
      const newX = stepX - dragState.dragOffset.x;
      const newY = stepY - dragState.dragOffset.y;

      // Update local drag position for smooth animation
      setDragState(prev => ({
        ...prev,
        currentPosition: { x: newX, y: newY }
      }));
    }

    // Handle connection dragging
    if (connectionDragState.isConnecting) {
      setConnectionDragState(prev => ({
        ...prev,
        mousePosition: { x: stepX, y: stepY }
      }));
    }

    // Handle canvas panning
    if (isPanning) {
      const deltaX = e.movementX;
      const deltaY = e.movementY;
      onPanChange({
        x: pan.x + deltaX,
        y: pan.y + deltaY
      });
    }
  }, [dragState, connectionDragState, mode, pan, zoom, onPanChange, isPanning]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    
    // Commit the drag position if we were dragging
    if (dragState.isDragging && dragState.draggedStep && dragState.currentPosition) {
      onStepUpdate(dragState.draggedStep.id, {
        position: dragState.currentPosition
      });
    }
    
    setDragState({
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      startPosition: { x: 0, y: 0 },
      currentPosition: undefined
    });

    // Note: Connection creation is now handled in handleConnectionEnd
    // Don't reset connection state here - let it persist until user clicks a target step or canvas
  }, [dragState, onStepUpdate]);

  // Handle connection click
  const handleConnectionClick = useCallback((connection: WorkflowConnection) => {
    onConnectionSelect(selectedConnection?.id === connection.id ? undefined : connection);
  }, [selectedConnection, onConnectionSelect]);

  // Handle canvas mouse down for panning
  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    if (mode === 'pan') {
      setIsPanning(true);
      e.preventDefault();
    }
  }, [mode]);

  // Handle canvas click (for deselecting and adding steps)
  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    console.log('Canvas click event:', {
      target: e.target,
      currentTarget: e.currentTarget,
      isCanvasClick: e.target === e.currentTarget,
      targetClassName: (e.target as HTMLElement).className,
      currentTargetClassName: (e.currentTarget as HTMLElement).className
    });
    
    // Handle clicks on the canvas itself or the grid background  
    const target = e.target as HTMLElement;
    let classNameStr = '';
    try {
      classNameStr = target.className ? target.className.toString() : '';
    } catch (err) {
      classNameStr = '';
    }
    const isCanvasOrGrid = e.target === e.currentTarget || 
                          classNameStr.includes('grid') ||
                          classNameStr.includes('bg-neutral-950');
    
    if (!isCanvasOrGrid) {
      return;
    }
    
    if (mode === 'select') {
      onStepSelect(undefined);
      onConnectionSelect(undefined);
    } else if (mode === 'connect') {
      // Reset connection state when clicking on empty canvas
      console.log('Resetting connection state - clicked on empty canvas');
      setConnectionDragState({
        isConnecting: false,
        mousePosition: { x: 0, y: 0 }
      });
    }
  }, [mode, onStepSelect, onConnectionSelect]);

  // Handle wheel for zooming
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    // Get mouse position relative to canvas
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate zoom change
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(0.1, Math.min(3, zoom + delta));
    
    if (newZoom !== zoom) {
      // Calculate new pan to keep mouse position fixed
      const zoomRatio = newZoom / zoom;
      const newPanX = mouseX - (mouseX - pan.x) * zoomRatio;
      const newPanY = mouseY - (mouseY - pan.y) * zoomRatio;
      
      onZoomChange(newZoom);
      onPanChange({ x: newPanX, y: newPanY });
    }
  }, [zoom, pan, onZoomChange, onPanChange]);

  // Handle canvas drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    try {
      const stepTemplate = JSON.parse(e.dataTransfer.getData('application/json'));
      const dropX = (e.clientX - rect.left - pan.x) / zoom;
      const dropY = (e.clientY - rect.top - pan.y) / zoom;

      const newStep: WorkflowStep = {
        id: `step_${Date.now()}`,
        type: stepTemplate.type,
        name: stepTemplate.name,
        description: stepTemplate.description,
        config: stepTemplate.defaultConfig,
        position: { x: dropX - 100, y: dropY - 40 }, // Center the step on cursor
        connections: [],
        conditions: [],
        retryConfig: {
          maxAttempts: 3,
          delay: 1000
        }
      };

      onStepAdd(newStep);
    } catch (error) {
      console.error('Failed to parse dropped step:', error);
    }
  }, [pan, zoom, onStepAdd]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  // Setup event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (dragState.isDragging || connectionDragState.isConnecting || isPanning) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [dragState.isDragging, connectionDragState.isConnecting, isPanning, handleMouseMove, handleMouseUp]);

  // Add keyboard event handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && connectionDragState.isConnecting) {
        console.log('Escape pressed - canceling connection');
        setConnectionDragState({
          isConnecting: false,
          mousePosition: { x: 0, y: 0 }
        });
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        // Handle deletion of selected items
        if (selectedConnection) {
          e.preventDefault();
          const sourceStep = workflow.definition.steps.find(s => s.id === selectedConnection.sourceStepId);
          const targetStep = workflow.definition.steps.find(s => s.id === selectedConnection.targetStepId);
          const connectionName = `${sourceStep?.name || 'Unknown'} → ${targetStep?.name || 'Unknown'}`;
          
          setConfirmDialog({
            isOpen: true,
            title: 'Delete Connection',
            message: `Are you sure you want to delete the connection "${connectionName}"? This action cannot be undone.`,
            variant: 'danger',
            onConfirm: () => {
              console.log('Deleting connection via Delete key:', connectionName);
              onConnectionDelete(selectedConnection.id);
              setConfirmDialog(prev => ({ ...prev, isOpen: false }));
            }
          });
        } else if (selectedStep) {
          e.preventDefault();
          setConfirmDialog({
            isOpen: true,
            title: 'Delete Step',
            message: `Are you sure you want to delete the step "${selectedStep.name}"? This action cannot be undone.`,
            variant: 'danger',
            onConfirm: () => {
              console.log('Deleting step via Delete key:', selectedStep.name);
              onStepDelete(selectedStep.id);
              setConfirmDialog(prev => ({ ...prev, isOpen: false }));
            }
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [connectionDragState.isConnecting, selectedConnection, selectedStep, workflow.definition.steps, onConnectionDelete, onStepDelete]);

  // Handle step right-click for deletion
  const handleStepRightClick = useCallback((e: React.MouseEvent, step: WorkflowStep) => {
    e.preventDefault();
    e.stopPropagation();
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Step',
      message: `Are you sure you want to delete the step "${step.name}"? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: () => {
        console.log('Deleting step via right-click:', step.name);
        onStepDelete(step.id);
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  }, [onStepDelete]);

  // Handle connection right-click for deletion
  const handleConnectionRightClick = useCallback((e: React.MouseEvent, connection: WorkflowConnection) => {
    e.preventDefault();
    e.stopPropagation();
    
    const sourceStep = workflow.definition.steps.find(s => s.id === connection.sourceStepId);
    const targetStep = workflow.definition.steps.find(s => s.id === connection.targetStepId);
    const connectionName = `${sourceStep?.name || 'Unknown'} → ${targetStep?.name || 'Unknown'}`;
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Connection',
      message: `Are you sure you want to delete the connection "${connectionName}"? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: () => {
        console.log('Deleting connection via right-click:', connectionName);
        onConnectionDelete(connection.id);
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  }, [workflow.definition.steps, onConnectionDelete]);

  // Render connection line
  const renderConnection = (connection: WorkflowConnection) => {
    const sourceStep = workflow.definition.steps.find(s => s.id === connection.sourceStepId);
    const targetStep = workflow.definition.steps.find(s => s.id === connection.targetStepId);

    if (!sourceStep || !targetStep) {
      return null;
    }

    // Use current positions (considering drag state)
    const sourcePosition = getCurrentStepPosition(sourceStep);
    const targetPosition = getCurrentStepPosition(targetStep);

    // Connect from right edge of source to left edge of target, centered vertically
    const sourceX = sourcePosition.x + 200; // Right edge of source step (width = 200px)
    const sourceY = sourcePosition.y + 40;  // Vertical center of source step (height = 80px)
    const targetX = targetPosition.x;       // Left edge of target step
    const targetY = targetPosition.y + 40;  // Vertical center of target step

    // Calculate curve control points for a smooth S-curve
    const deltaX = targetX - sourceX;
    const deltaY = targetY - sourceY;
    const controlOffset = Math.max(50, Math.abs(deltaX) * 0.3); // Adaptive curve strength
    
    const controlX1 = sourceX + controlOffset;
    const controlY1 = sourceY;
    const controlX2 = targetX - controlOffset;
    const controlY2 = targetY;

    // Create the curved path
    const pathData = `M ${sourceX} ${sourceY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${targetX} ${targetY}`;

    // Calculate the angle at the end of the curve for arrow direction
    // Use the direction from the last control point to the target
    const arrowAngle = Math.atan2(targetY - controlY2, targetX - controlX2) * (180 / Math.PI);

    const isSelected = selectedConnection?.id === connection.id;

    return (
      <g key={connection.id}>
        {/* Connection path */}
        <path
          d={pathData}
          stroke={isSelected ? '#3b82f6' : '#6b7280'}
          strokeWidth={isSelected ? 3 : 2}
          strokeDasharray={connection.condition ? '5,5' : 'none'}
          fill="none"
          className="cursor-pointer hover:stroke-blue-400 transition-colors"
          onClick={() => handleConnectionClick(connection)}
          onContextMenu={(e) => {
            e.preventDefault();
            handleConnectionRightClick(e as any, connection);
          }}
          markerEnd={isSelected ? "url(#arrowhead-selected)" : "url(#arrowhead)"}
        />
        
        {/* Selection indicator for selected connections */}
        {isSelected && (
          <>
            {/* Wider background path for better visibility */}
            <path
              d={pathData}
              stroke="#3b82f6"
              strokeWidth={6}
              strokeOpacity={0.3}
              fill="none"
              className="pointer-events-none"
            />
            
            {/* Selection dots at midpoint */}
            <circle
              cx={(sourceX + targetX) / 2}
              cy={(sourceY + targetY) / 2}
              r={4}
              fill="#3b82f6"
              className="pointer-events-none"
            />
            <circle
              cx={(sourceX + targetX) / 2}
              cy={(sourceY + targetY) / 2}
              r={2}
              fill="white"
              className="pointer-events-none"
            />
          </>
        )}
        
        {/* Connection label */}
        {connection.label && (
          <text
            x={(sourceX + targetX) / 2}
            y={(sourceY + targetY) / 2 - 10}
            textAnchor="middle"
            className="text-xs fill-neutral-400 pointer-events-none"
          >
            {connection.label}
          </text>
        )}
      </g>
    );
  };

  // Render temporary connection line while dragging
  const renderTempConnection = () => {
    if (!connectionDragState.isConnecting || !connectionDragState.sourceStep) {
      return null;
    }

    // Use current position (considering drag state)
    const sourcePosition = getCurrentStepPosition(connectionDragState.sourceStep);
    const sourceX = sourcePosition.x + 200; // Right edge of source step
    const sourceY = sourcePosition.y + 40;  // Vertical center of source step
    const targetX = connectionDragState.mousePosition.x;
    const targetY = connectionDragState.mousePosition.y;

    // Calculate curve control points for a smooth S-curve (same as regular connections)
    const deltaX = targetX - sourceX;
    const deltaY = targetY - sourceY;
    const controlOffset = Math.max(50, Math.abs(deltaX) * 0.3); // Adaptive curve strength
    
    const controlX1 = sourceX + controlOffset;
    const controlY1 = sourceY;
    const controlX2 = targetX - controlOffset;
    const controlY2 = targetY;

    // Create the curved path
    const pathData = `M ${sourceX} ${sourceY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${targetX} ${targetY}`;

    return (
      <path
        d={pathData}
        stroke="#3b82f6"
        strokeWidth={2}
        strokeDasharray="5,5"
        fill="none"
        className="pointer-events-none"
        markerEnd="url(#arrowhead-selected)"
      />
    );
  };

  return (
    <div
      ref={canvasRef}
      className={`
        workflow-canvas relative w-full h-full overflow-hidden bg-neutral-950
        ${mode === 'pan' ? 'cursor-grab' : ''}
        ${mode === 'connect' ? 'cursor-crosshair' : ''}
        ${isPanning ? 'cursor-grabbing' : ''}
      `}
      onClick={handleCanvasClick}
      onWheel={handleWheel}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onMouseDown={handleCanvasMouseDown}
    >
      {/* Grid background */}
      <div
        className="absolute inset-0 opacity-20 cursor-pointer"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
          backgroundPosition: `${pan.x}px ${pan.y}px`
        }}
        onClick={(e) => {
          console.log('Grid background clicked');
          if (mode === 'connect' && connectionDragState.isConnecting) {
            console.log('Canceling connection from grid click');
            setConnectionDragState({
              isConnecting: false,
              mousePosition: { x: 0, y: 0 }
            });
          }
        }}
      />

      {/* Canvas content */}
      <div className="absolute inset-0">
        {/* SVG for connections */}
        <svg 
          className="absolute inset-0 w-full h-full"
          style={{ 
            width: '100%', 
            height: '100%',
            pointerEvents: 'none', // SVG doesn't block other interactions
            zIndex: 1,
            overflow: 'visible' // Allow lines to extend beyond viewport
          }}
        >
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill="#6b7280"
              />
            </marker>
            <marker
              id="arrowhead-selected"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill="#3b82f6"
              />
            </marker>
          </defs>
          
          {/* SVG content with same transform as steps */}
          <g 
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
              pointerEvents: 'auto' // Enable pointer events for connections
            }}
          >
            {/* Render all connections */}
            {workflow.definition.steps.flatMap(step => {
              return step.connections.map((connection, index) => {
                const result = renderConnection(connection);
                return result;
              });
            })}
            
            {/* Render temporary connection */}
            {renderTempConnection()}
          </g>
        </svg>

        {/* Workflow steps */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            zIndex: 10
          }}
        >
          {workflow.definition.steps.map((step, index) => {
            const Icon = getStepIcon(step.type);
            const isSelected = selectedStep?.id === step.id;
            const isHovered = hoveredStep === step.id;
            const isDragging = dragState.draggedStep?.id === step.id;

            // Use current drag position if dragging, otherwise use step position
            const currentPosition = getCurrentStepPosition(step);

            return (
              <div
                key={step.id}
                className={`
                  absolute w-[200px] h-[80px] cursor-pointer
                  select-none
                  ${isDragging ? 'z-50' : 'z-20'}
                  ${isDragging ? 'opacity-75' : ''}
                  pointer-events-auto
                `}
                style={{
                  left: `${currentPosition?.x || 0}px`,
                  top: `${currentPosition?.y || 0}px`,
                }}
                onMouseDown={(e) => handleStepMouseDown(e, step)}
                onMouseEnter={() => setHoveredStep(step.id)}
                onMouseLeave={() => setHoveredStep(undefined)}
                onContextMenu={(e) => handleStepRightClick(e, step)}
              >
                <div
                  className={`
                    relative p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer
                    ${selectedStep?.id === step.id 
                      ? 'border-blue-500 bg-gradient-to-br from-blue-900/20 to-purple-900/20 shadow-lg shadow-blue-500/20' 
                      : 'border-neutral-700 bg-gradient-to-br from-neutral-800 to-neutral-900 hover:border-neutral-600'
                    }
                    ${connectionDragState.isConnecting && connectionDragState.sourceStep?.id === step.id
                      ? 'ring-2 ring-blue-400 ring-opacity-75 border-blue-400'
                      : ''
                    }
                    ${connectionDragState.isConnecting && connectionDragState.sourceStep?.id !== step.id
                      ? 'ring-2 ring-green-400 ring-opacity-50 border-green-400 hover:ring-opacity-75'
                      : ''
                    }
                  `}
                >
                  {/* Step header */}
                  <div className={`
                    h-12 rounded-t-lg bg-gradient-to-r ${getStepColor(step)}
                    flex items-center justify-between px-3
                  `}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-white" />
                      <span className="text-white text-sm font-medium truncate">
                        {step.name}
                      </span>
                    </div>
                    
                    {/* Step actions */}
                    <div className="flex items-center gap-1">
                      {/* Active indicator - could be based on workflow status */}
                      <div className="w-2 h-2 bg-neutral-500 rounded-full" />
                      
                      {isSelected && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle edit
                            }}
                            className="p-1 hover:bg-white/20 rounded transition-colors"
                          >
                            <Edit className="w-3 h-3 text-white" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onStepDelete(step.id);
                            }}
                            className="p-1 hover:bg-red-500/20 rounded transition-colors"
                          >
                            <Trash2 className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Step content */}
                  <div className="h-7 bg-neutral-900 rounded-b-lg p-2">
                    <p className="text-neutral-300 text-xs leading-tight">
                      {step.description || `${step.type} step`}
                    </p>
                  </div>

                  {/* Connection points */}
                  {mode === 'connect' && (
                    <>
                      {/* Input connection point */}
                      <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-md" />
                      
                      {/* Output connection point */}
                      <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-md" />
                    </>
                  )}

                  {/* Connection source indicator */}
                  {connectionDragState.isConnecting && connectionDragState.sourceStep?.id === step.id && (
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Empty state */}
      {workflow.definition.steps.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-neutral-500">
            <Zap className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-semibold mb-2">Empty Canvas</h3>
            <p className="text-sm">
              Drag steps from the palette or use AI to generate your workflow
            </p>
          </div>
        </div>
      )}

      {/* Canvas info overlay */}
      <div className="absolute top-4 left-4 bg-neutral-900/80 backdrop-blur-sm rounded-lg p-3 text-sm text-neutral-300">
        <div className="flex items-center gap-4">
          <span>Mode: {mode}</span>
          <span>Zoom: {Math.round(zoom * 100)}%</span>
          <span>Steps: {workflow.definition.steps.length}</span>
          {connectionDragState.isConnecting && (
            <span className="text-blue-400 font-medium">
              Connecting from: {connectionDragState.sourceStep?.name}
            </span>
          )}
          {selectedConnection && (
            <span className="text-blue-400 font-medium">
              Connection selected
            </span>
          )}
        </div>
      </div>

      {/* Mode instructions */}
      <div className="absolute bottom-4 right-4 bg-neutral-900/80 backdrop-blur-sm rounded-lg p-3 text-xs text-neutral-400">
        {mode === 'select' && (
          <div>
            <p>• Click to select steps or connections</p>
            <p>• Drag to move steps</p>
            <p>• Right-click to delete steps/connections</p>
            <p>• Delete key to remove selected items</p>
          </div>
        )}
        {mode === 'connect' && (
          <div>
            <p>• Click source step, then target</p>
            <p>• Creates connections between steps</p>
            <p>• Right-click connections to delete</p>
            <p>• Click empty space or press Escape to cancel</p>
          </div>
        )}
        {mode === 'pan' && (
          <div>
            <p>• Drag to pan the canvas</p>
            <p>• Scroll to zoom in/out</p>
            <p>• Switch modes to edit</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}; 